/**
 * Strategic Planning - the two genuinely new registries this round added:
 * multi-year goal/OKR tracking and an enterprise risk register (likelihood
 * x impact), both distinct from the existing compliance/accreditation
 * register (routers/compliance.py, regulatory deadlines) and the
 * student-level at-risk composite (routers/analytics.py).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import operationalInsightsService from '../services/operationalInsightsService';
import { CircleNotch } from '@phosphor-icons/react';
import './StrategicPlanningPage.css';

const GOAL_CATEGORIES = ['academic', 'financial', 'operational', 'hr', 'community', 'other'];
const GOAL_STATUSES = ['not_started', 'on_track', 'at_risk', 'achieved', 'missed'];
const RISK_CATEGORIES = ['operational', 'financial', 'reputational', 'safety', 'strategic', 'compliance', 'other'];
const RISK_STATUSES = ['open', 'mitigating', 'closed'];

function StrategicPlanningPage() {
  const [tab, setTab] = useState('goals');

  return (
    <Layout>
      <div className="page strategic-planning-page">
        <div className="page-header">
          <div>
            <h1>Strategic Planning</h1>
            <p>Multi-year goals with progress tracking, and an enterprise risk register scored by likelihood × impact</p>
          </div>
        </div>

        <div className="spp-tabs">
          <button type="button" className={tab === 'goals' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setTab('goals')}>Strategic Goals</button>
          <button type="button" className={tab === 'risks' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setTab('risks')}>Risk Register</button>
        </div>

        {tab === 'goals' && <GoalsTab />}
        {tab === 'risks' && <RisksTab />}
      </div>
    </Layout>
  );
}

function GoalsTab() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'other', target_metric: '', target_value: '', current_value: '', unit: '', start_date: '', target_date: '' });
  const [submitting, setSubmitting] = useState(false);
  const [progressTarget, setProgressTarget] = useState(null);
  const [progressValue, setProgressValue] = useState('');
  const [progressStatus, setProgressStatus] = useState('on_track');

  const load = useCallback(async () => {
    setLoading(true);
    try { setGoals(await operationalInsightsService.listGoals()); }
    catch (error) { toast.error('Failed to load goals: ' + error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_date || !form.target_date) { toast.error('Title, start date and target date are required'); return; }
    setSubmitting(true);
    try {
      await operationalInsightsService.createGoal({
        ...form,
        target_value: form.target_value ? parseFloat(form.target_value) : null,
        current_value: form.current_value ? parseFloat(form.current_value) : null,
      });
      toast.success('Goal created');
      setShowCreate(false);
      setForm({ title: '', description: '', category: 'other', target_metric: '', target_value: '', current_value: '', unit: '', start_date: '', target_date: '' });
      load();
    } catch (error) {
      toast.error('Failed to create goal: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openProgress = (goal) => {
    setProgressTarget(goal);
    setProgressValue(goal.current_value ?? '');
    setProgressStatus(goal.status);
  };

  const submitProgress = async () => {
    try {
      await operationalInsightsService.updateGoalProgress(progressTarget.id, { current_value: parseFloat(progressValue), status: progressStatus });
      toast.success('Progress updated');
      setProgressTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to update progress: ' + error.message);
    }
  };

  const remove = async (id) => {
    try { await operationalInsightsService.deleteGoal(id); toast.success('Deleted'); load(); }
    catch (error) { toast.error('Failed to delete: ' + error.message); }
  };

  return (
    <div className="spp-panel">
      <button className="btn btn-primary" onClick={() => setShowCreate(true)}>New Goal</button>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : goals.length === 0 ? (
        <div className="empty-state"><p>No strategic goals set yet</p></div>
      ) : (
        <div className="spp-table"><table>
          <thead><tr><th>Title</th><th>Category</th><th>Target</th><th>Current</th><th>Progress</th><th>Status</th><th>Target Date</th><th>Actions</th></tr></thead>
          <tbody>{goals.map((g) => (
            <tr key={g.id}>
              <td>{g.title}</td><td>{g.category}</td>
              <td>{g.target_value != null ? `${g.target_value}${g.unit || ''}` : '—'}</td>
              <td>{g.current_value != null ? `${g.current_value}${g.unit || ''}` : '—'}</td>
              <td>{g.progress_pct != null ? `${g.progress_pct}%` : '—'}</td>
              <td><span className={`badge status-${g.status}`}>{g.status.replace(/_/g, ' ').toUpperCase()}</span></td>
              <td>{g.target_date}</td>
              <td className="spp-row-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => openProgress(g)}>Update Progress</button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(g.id)}>Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>New Strategic Goal</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <div className="form-group"><label>Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div className="form-group"><label>Description</label><textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {GOAL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Target Metric</label><input value={form.target_metric} onChange={(e) => setForm({ ...form, target_metric: e.target.value })} placeholder="e.g. fee_collection_rate" /></div>
              <div className="spp-picker-row">
                <div className="form-group"><label>Target Value</label><input type="number" step="0.1" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} /></div>
                <div className="form-group"><label>Current Value</label><input type="number" step="0.1" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} /></div>
                <div className="form-group"><label>Unit</label><input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="%" /></div>
              </div>
              <div className="spp-picker-row">
                <div className="form-group"><label>Start Date *</label><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></div>
                <div className="form-group"><label>Target Date *</label><input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} required /></div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {progressTarget && (
        <div className="modal-overlay" onClick={() => setProgressTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Update Progress: {progressTarget.title}</h2><button className="btn-close" onClick={() => setProgressTarget(null)}>×</button></div>
            <div className="form-group"><label>Current Value</label><input type="number" step="0.1" value={progressValue} onChange={(e) => setProgressValue(e.target.value)} /></div>
            <div className="form-group">
              <label>Status</label>
              <select value={progressStatus} onChange={(e) => setProgressStatus(e.target.value)}>
                {GOAL_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setProgressTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitProgress}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RisksTab() {
  const [risks, setRisks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'operational', description: '', likelihood: 3, impact: 3, mitigation_plan: '', review_date: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editStatus, setEditStatus] = useState('open');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([operationalInsightsService.listRisks(), operationalInsightsService.getRiskSummary()]);
      setRisks(r || []);
      setSummary(s);
    } catch (error) {
      toast.error('Failed to load risk register: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      await operationalInsightsService.createRisk(form);
      toast.success('Risk added');
      setShowCreate(false);
      setForm({ title: '', category: 'operational', description: '', likelihood: 3, impact: 3, mitigation_plan: '', review_date: '' });
      load();
    } catch (error) {
      toast.error('Failed to add risk: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (risk) => { setEditTarget(risk); setEditStatus(risk.status); };

  const submitEdit = async () => {
    try {
      await operationalInsightsService.updateRisk(editTarget.id, { status: editStatus });
      toast.success('Updated');
      setEditTarget(null);
      load();
    } catch (error) {
      toast.error('Failed to update: ' + error.message);
    }
  };

  const remove = async (id) => {
    try { await operationalInsightsService.deleteRisk(id); toast.success('Deleted'); load(); }
    catch (error) { toast.error('Failed to delete: ' + error.message); }
  };

  return (
    <div className="spp-panel">
      {summary && (
        <div className="spp-stat-row">
          <div className="spp-stat"><span className="spp-stat-value">{summary.total_open_risks}</span><span className="spp-stat-label">Open Risks</span></div>
          <div className="spp-stat"><span className="spp-stat-value">{summary.high_risk_count}</span><span className="spp-stat-label">High Risk (score ≥15)</span></div>
        </div>
      )}
      <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add Risk</button>

      {loading ? (
        <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
      ) : risks.length === 0 ? (
        <div className="empty-state"><p>No risks logged yet</p></div>
      ) : (
        <div className="spp-table"><table>
          <thead><tr><th>Title</th><th>Category</th><th>Likelihood</th><th>Impact</th><th>Score</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{risks.map((r) => (
            <tr key={r.id}>
              <td>{r.title}</td><td>{r.category}</td><td>{r.likelihood}</td><td>{r.impact}</td>
              <td><span className={r.risk_score >= 15 ? 'spp-high-score' : ''}>{r.risk_score}</span></td>
              <td><span className={`badge status-${r.status}`}>{r.status.toUpperCase()}</span></td>
              <td className="spp-row-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => openEdit(r)}>Update Status</button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(r.id)}>Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Add Risk</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
            <form onSubmit={submitCreate}>
              <div className="form-group"><label>Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {RISK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Description</label><textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="spp-picker-row">
                <div className="form-group"><label>Likelihood (1-5)</label><input type="number" min="1" max="5" value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: parseInt(e.target.value, 10) || 1 })} /></div>
                <div className="form-group"><label>Impact (1-5)</label><input type="number" min="1" max="5" value={form.impact} onChange={(e) => setForm({ ...form, impact: parseInt(e.target.value, 10) || 1 })} /></div>
              </div>
              <div className="form-group"><label>Mitigation Plan</label><textarea rows="2" value={form.mitigation_plan} onChange={(e) => setForm({ ...form, mitigation_plan: e.target.value })} /></div>
              <div className="form-group"><label>Review Date</label><input type="date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} /></div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Update: {editTarget.title}</h2><button className="btn-close" onClick={() => setEditTarget(null)}>×</button></div>
            <div className="form-group">
              <label>Status</label>
              <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                {RISK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategicPlanningPage;
